/* eslint-disable max-lines */
import { FilterOutlined, HighlightOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons'
import { PermissionActionEnum, type ViewFieldsFragment } from '@salescore/client-api'
import {
  type CompiledKpiViewConfig,
  CORE_CONSTANT,
  getMetricFromKpiFormula,
  viewConfigKpiWithMetricToInline,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithId,
  type ViewUiKpi,
} from '@salescore/core'
import { Empty, Space, Table, Tooltip } from 'antd'
import type { HashMap } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import type {
  PivotColumn,
  PivotRowColumn,
  PivotSystemColumn,
  RSheetColumn,
  RSheetRecordNode,
} from '../../../domain/column.ts'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import { useProgress } from '../../../hooks/useProgress.ts'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import type { KpiChartDisplaySettingPresetsState } from '../../../kpi/kpiChartDisplaySettingPresets'
import { extractKpiId } from '../../../kpi/utility.ts'
import { useGetInnerCells, useRowExpandable } from '../../../recoil/cursor.ts'
import { kpiParameterAtom } from '../../../recoil/navigation/atoms.ts'
import { useCalculatedRowHeight, useSetRowConfig } from '../../../recoil/navigation/hooks.ts'
import { additionalConfigAtom } from '../../../recoil/records/atoms'
import type { LatestFieldChangeItem } from '../../../recoil/recordsHistory.ts'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewConfigKpi } from '../../../recoil/view/hooks/useViewConfigKpi'
import { useResizeColumnMutation } from '../../../recoil/viewConfig.ts'
import { MINIMUM_WIDTH, ROW_HEIGHT } from '../../../rsheet/components/body/const.tsx'
import { DraggableBar } from '../../../rsheet/components/header/DraggableBar.tsx'
import { DraggableHeaderColumn } from '../../../rsheet/components/header/DraggableHeaderColumn.tsx'
import { KpiHeaderContextMenu } from '../../../rsheet/components/header/HeaderContextMenu.tsx'
import {
  type BlockHeaderStyle,
  DEFAULT_HEADER_COLOR,
} from '../../../rsheet/components/header/RSheetsNodeBlockHeader.tsx'
import { RSheet } from '../../../rsheet/components/RSheet'
import {
  type SetMainCursorByRecordIdArguments,
  useSetMainCursorByRecordId,
} from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { ViewComponent } from '../../ViewComponent'
import type { KpiDisplayFormat, KpiPieChartCategoryFilters } from '../ViewUIKpi'
import { KpiPieChart } from './KpiPieChart'
import { KpiStackedChart } from './KpiStackedChart'
import { KpiTableCellGroup } from './KpiTableCellGroup.tsx'

const dynamicViewFields: ViewFieldsFragment = {
  id: '',
  viewGroupId: ``,
  type: `sheet`,
  name: ``,
  config: { type: `sheet` },
  rank: 0,
  defaultPermission: PermissionActionEnum.None,
  importedSourceId: ``,
  emoji: ``,
  roleRecordPermissions: [],
  private: false,
  archived: false,
  archivedAt: null,
}

export const ViewUIKpiBody = ({
  component,
  format,
  kpiPieChartCategoryFilters,
  kpiChartDisplaySettingPresetsState,
  columns,
  compiledConfig,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  component: ViewUiKpi
  format: KpiDisplayFormat | undefined
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  kpiChartDisplaySettingPresetsState: KpiChartDisplaySettingPresetsState
  columns: Array<
    // eslint-disable-next-line sonarjs/use-type-alias
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  compiledConfig: CompiledKpiViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  switch (format) {
    case 'sheet': {
      return <KpiSheet view={view} ability={ability} />
    }
    case 'table': {
      return (
        <KpiTable
          component={component}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )
    }
    case 'combo': {
      return (
        <KpiStackedChart
          pivot={component.pivot}
          chartDesignSettings={kpiChartDisplaySettingPresetsState.combo.design}
          view={view}
        />
      )
    }
    case 'pie': {
      return (
        <KpiPieChart
          pivot={component.pivot}
          kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
          chartDesignSettings={kpiChartDisplaySettingPresetsState.pie.design}
          view={view}
        />
      )
    }
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    default: {
      return (
        <KpiTable
          component={component}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )
    }
  }
}

const KpiSheet = ({
  view,
  ability,
}: {
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const viewsContext = useRecoilValue(viewsContextAtom)
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const [kpiViewConfig, setKpiViewConfig] = useViewConfigKpi()
  const currentFormula = kpiViewConfig.formula
  const dynamicViewId = CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX + view.id

  if (currentFormula.type !== 'ref') {
    // ref 以外 (= 未設定 KPI の formula empty) は metric を持たないため、ここでシート表示を打ち切る。
    // 以降の closure は ref に narrow された currentFormula.metric を前提にするため、この guard で確定させる。
    return <Empty description={<div>{t(`シートが見つかりません。`)}</div>} />
  }
  const currentMetric = currentFormula.metric

  // useEffect(() => {
  //   const existRenderDuration = findLastDurationMetrics(view.id)
  //   if (isNull(existRenderDuration)) {
  //     return
  //   }
  //   // すでに記録を開始しているdurationのviewIdも変化するので、更新しておく
  //   updateDurationMetrics(
  //     {
  //       ...existRenderDuration,
  //       viewId: dynamicViewId,
  //     },
  //     view.id,
  //   )
  // }, [dynamicViewId])

  return (
    <ViewComponent
      view={{
        ...dynamicViewFields,
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: ここで指定するidをもとのKPIのIDと同じにすると、不具合の際に誤ってシートの情報でKPIを上書きしてしまいそうで、一応別のIDを割り振っている
        //       しかし、ここで「新しいIDをprefix+idとする」というロジックが暗黙的でわかりづらそう
        id: dynamicViewId,
        name: `${view.name}の一覧`,
        config: currentMetric.sheet,
        defaultPermission: view.defaultPermission,
      }}
      parentAbility={ability}
      parameter={{}}
      additionalConfig={{
        // ドリルダウンのフィルタ、期間フィルタを引き継がせる
        // 期間フィルタは「棒グラフ側で期間設定するとシートに引き継がれる」「シートで設定してもKPIに引き継がれない」という微妙な挙動になるが、許容する
        ...additionalConfig,
        // previousConfigにkpiViewConfigを指定することで、各種のKPIの設定を使ったり、KPIビューとしてのシートであることを指定する
        // 2023/04 例えば「KPIのときは集計表示をしない」というロジックもpreviousConfigで指定している
        previousConfig: kpiViewConfig,
      }}
      // onFinish={onFinish}
      viewsContext={{
        ...viewsContext,
        isSaveConfigButtonActive: false,
        updateView: async (argument): Promise<ViewFieldsFragment | undefined> => {
          const sheetViewConfig = argument.config
          if (sheetViewConfig?.type !== 'sheet') {
            return // ありえないはず
          }
          if (view.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX)) {
            // プレビューのドリルダウンの場合は、元の KPI のフォームに変更を加えるため、プレビュー側の updateView に処理をお任せする
            await viewsContext.updateView(argument)
            return
          }
          const nextKpiConfig = {
            ...kpiViewConfig,
            formula: {
              ...currentFormula,
              metric: {
                ...currentMetric,
                sheet: {
                  ...currentMetric.sheet,
                  tree: sheetViewConfig.tree,
                  fields: sheetViewConfig.fields,
                  // ダッシュボードのモーダルからこの KPI ビューが開かれている場合、絞り込みの設定は反映しないのが仕様である
                  filterTree:
                    additionalConfig.previousDimmensions === undefined
                      ? sheetViewConfig.filterTree
                      : currentMetric.sheet.filterTree,
                  sorters: sheetViewConfig.sorters,
                  meta: {
                    fixedLeftColumnIndex: sheetViewConfig.meta?.fixedLeftColumnIndex,
                    visibleRowNum: sheetViewConfig.meta?.visibleRowNum,
                  },
                },
              },
            },
          }
          // 変更フラグ経由の別保存にすると、一覧側が実保存を待てず loading が先に解除される。
          const savedView = await viewsContext.updateView({
            id: view.id,
            config: viewConfigKpiWithMetricToInline(nextKpiConfig),
            userIntended: argument.userIntended,
          })
          if (savedView !== undefined) {
            setKpiViewConfig(nextKpiConfig)
          }
          return savedView
        },
      }}
    />
  )
}

const BLOCK_HEADER_STYLE: BlockHeaderStyle = {
  treeHeaderHeightPerNode: 42,
  blockStyle: {
    paddingLeft: 8,
    paddingTop: 12,
    fontSize: 14,
  },
  renderCellLabel: (cell) => cell.label,
}

export const KpiTable = ({
  component,
  columns,
  compiledConfig,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  component: ViewUiKpi
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  compiledConfig: CompiledKpiViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const setRowConfig = useSetRowConfig()
  const rowHeight = useCalculatedRowHeight(compiledConfig)
  const getInnerCells = useGetInnerCells(undefined)
  const rowExpandable = useRowExpandable(columns, undefined)
  const progress = useProgress(compiledConfig)
  const [config] = useViewConfigKpi()
  const queryResult = useViewQueryResult(view)

  const isSettingUncompleted = getMetricFromKpiFormula(config.formula) === undefined
  const isEmptyRecords = queryResult !== undefined && columns.length === 0

  const isEmptyTable = isSettingUncompleted || isEmptyRecords
  const context = useMemo(
    () => ({
      type: 'kpi' as const,
      kpi: component.kpi,
      pivot: component.pivot,
      fixedLeftColumnIndex: component.pivot.rows.length + 1,
    }),
    [component.kpi, component.pivot],
  )
  const setMainCursorByRecordId = useSetMainCursorByRecordId(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    ability,
    undefined,
    undefined,
  )

  const renderHeaderCell = useCallback(
    (
      column:
        PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>,
      columnIndex: number,
    ) => (
      <KpiHeaderCell
        column={column}
        columnIndex={columnIndex}
        columns={columns}
        compiledConfig={compiledConfig}
        view={view}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    ),
    [columns, compiledConfig, view],
  )
  const renderCellGroup = useCallback(
    ({
      row,
      column,
      rowIndex,
      columnIndex,
      expandable,
      expanded,
      latestFieldChangesRow,
      setDragging,
      setMainCursorByRecordId,
      expandOnDrag,
    }: {
      row: ViewQueryRecordNodeWithId
      column:
        PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
      rowIndex: number
      columnIndex: number
      expandable: boolean
      expanded: boolean
      latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
      setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
      setDragging: () => void
      expandOnDrag: (position: RSheetsCursorPosition) => void
    }): ReactElement => (
      <KpiTableCellGroup
        row={row}
        column={column}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        getInnerCells={getInnerCells}
        expandable={expandable}
        expanded={expanded}
        latestFieldChangesRow={latestFieldChangesRow}
        rowHeight={rowHeight}
        setMainCursorByRecordId={setMainCursorByRecordId}
        setRowConfig={setRowConfig}
        view={view}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        context={context}
        columns={columns}
        rootRecordNode={row}
        progress={progress}
        ability={ability}
      />
    ),
    [ability, columns, context, getInnerCells, progress, rowHeight, setRowConfig, view],
  )

  return (
    <div className="h-full">
      {isEmptyTable ? (
        <div className="p-8">
          <Table />
        </div>
      ) : (
        <RSheet
          // eslint-disable-next-line sonarjs/todo-tag
          height="100%" // TODO
          columns={columns}
          context={context}
          fixedFooter={() => null}
          footer={() => null}
          columnVirtualization={false}
          compiledConfig={compiledConfig}
          renderHeaderCell={renderHeaderCell}
          view={view}
          blockHeaderStyle={BLOCK_HEADER_STYLE}
          visibleRowNumber={undefined}
          rowExpandable={rowExpandable}
          renderCellGroup={renderCellGroup}
          setMainCursorByRecordId={setMainCursorByRecordId}
          rowHeight={rowHeight}
          ability={ability}
          query={undefined}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )}
    </div>
  )
}

const KpiHeaderCell = ({
  column,
  columnIndex,
  columns,
  compiledConfig,
  view,
  setHasChangeToViewSchema,
}: {
  column:
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  columnIndex: number
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  compiledConfig: CompiledKpiViewConfig
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const resizeColumnMutation = useResizeColumnMutation(compiledConfig, view)
  const { width } = column
  const [openColumnContextMenu, setOpenColumnContextMenu] = useState(false)
  const [headerFocused, setHeaderFocused] = useState(false)

  const [kpiParameter] = useRecoilState(kpiParameterAtom)

  const kpiId = useMemo(() => extractKpiId(kpiParameter, column), [column, kpiParameter])
  const isCommitmentKpiLabeldKpi = kpiParameter.goalConfig?.commitmentKpiLabel?.viewId === kpiId

  // Tooltipの領域がコンテキストメニューまで含んでおり、コンテキストメニューを開いた際にフォーカスが残ったままになってしまうこと防ぐ
  const tooltipVisibility =
    column.columnType === 'pivot' &&
    column.helpText !== undefined &&
    column.helpText !== '' &&
    headerFocused &&
    !openColumnContextMenu

  return (
    <Tooltip
      title={column.columnType === 'pivot' ? column.helpText : undefined}
      placement="right"
      open={tooltipVisibility}
    >
      <div
        onMouseEnter={() => {
          setHeaderFocused(true)
        }}
        onMouseLeave={() => {
          setHeaderFocused(false)
        }}
        onDragOver={(e) => {
          e.preventDefault() // onDropを発火させるのに必要
        }}
        onDrop={(e) => {
          // onDropで特にロジックは書かないが、onDropの定義はドロップアニメーション(要素の元の位置に戻るアニメーション)をdisableするために必要
          e.preventDefault()
        }}
        style={{
          width,
          position: 'relative',
          transition: RSheetsStyle.cellTransition,
        }}
      >
        <DraggableHeaderColumn
          width={width}
          height={ROW_HEIGHT}
          widths={columns.map((x) => x.width)}
          column={column}
          columnIndex={columnIndex}
          onClick={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          onContextMenu={() => {
            setOpenColumnContextMenu(true)
            setHeaderFocused(false)
          }}
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
        <div
          className="p-1"
          style={{
            borderRight: `1px ${DEFAULT_HEADER_COLOR.borderColor} solid`,
            color: openColumnContextMenu ? DEFAULT_HEADER_COLOR.textColorWhite : DEFAULT_HEADER_COLOR.textColor,
            backgroundColor: openColumnContextMenu ? DEFAULT_HEADER_COLOR.bgColorDark : DEFAULT_HEADER_COLOR.bgColor,
            padding: '0px 4px',
            // backgroundColor: '#F6F8F9',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            height: ROW_HEIGHT - 2,
            display: 'flex',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          <KpiHeaderCellBody column={column} isCommitmentKpiLabeledKpi={isCommitmentKpiLabeldKpi} />
        </div>
        <DraggableBar
          width={width}
          height={ROW_HEIGHT}
          setWidth={(width) => {
            resizeColumnMutation({ column, width, allColumnIndex: columnIndex, setHasChangeToViewSchema })
          }}
        />
        {openColumnContextMenu && (column.columnType === 'pivot_row' || column.columnType === 'pivot') && (
          <KpiHeaderContextMenu
            column={column}
            hide={() => {
              setOpenColumnContextMenu(false)
              setHeaderFocused(false)
            }}
            columns={columns}
            compiledConfig={compiledConfig}
            view={view}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        )}
      </div>
    </Tooltip>
  )
}

const KpiHeaderCellBody = <T extends RSheetRecordNode>({
  column,
  isCommitmentKpiLabeledKpi,
}: {
  column: RSheetColumn<T>
  isCommitmentKpiLabeledKpi: boolean
}): ReactElement => {
  if (column.metaType === 'record_url') {
    return <></>
  }
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }

  return (
    <div className={`flex pl-1 align-middle`}>
      <Space>
        <span className={`whitespace-normal break-all ${isCommitmentKpiLabeledKpi ? 'font-bold' : ''}`}>
          {column.title ?? ''}
          {column.required === true ? '*' : ''}
        </span>
        {column.isFiltered === true && <FilterOutlined style={{ opacity: 0.5 }} />}
        {column.sortState === 'asc' ? (
          <SortAscendingOutlined />
        ) : // eslint-disable-next-line sonarjs/no-nested-conditional
        column.sortState === 'desc' ? (
          <SortDescendingOutlined />
        ) : (
          <></>
        )}
        {column.isHighlighted === true && <HighlightOutlined style={{ opacity: 0.5 }} />}
      </Space>
    </div>
  )
}
